document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const FREE_SHIPPING_THRESHOLD = 7500;
const shopRoot = window.Shopify?.routes?.root || '/';
let activeOverlay = null;
let activeOverlayTrigger = null;

const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  "'": '&#39;',
  '"': '&quot;',
}[character]));

const formatMoney = (cents) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(Number(cents || 0) / 100);

const focusableElements = (container) => Array.from(container.querySelectorAll(
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
)).filter((element) => !element.hidden && element.offsetParent !== null);

function openOverlay(overlay, trigger) {
  if (!overlay) return;
  let returnFocus = trigger || document.activeElement;
  if (activeOverlay && activeOverlay !== overlay && activeOverlay.contains(returnFocus)) {
    returnFocus = activeOverlayTrigger;
  }
  if (activeOverlay && activeOverlay !== overlay) closeOverlay(activeOverlay, false);

  activeOverlay = overlay;
  activeOverlayTrigger = returnFocus;
  overlay.hidden = false;
  document.body.classList.add('is-locked');

  window.requestAnimationFrame(() => {
    if (activeOverlay !== overlay || overlay.hidden) return;
    const preferredFocus = overlay.querySelector('input:not([type="hidden"]), [data-overlay-initial-focus]');
    const firstFocusable = focusableElements(overlay)[0];
    (preferredFocus || firstFocusable)?.focus();
  });
}

function closeOverlay(overlay = activeOverlay, restoreFocus = true) {
  if (!overlay) return;
  overlay.hidden = true;
  if (overlay.matches('[data-mobile-menu]')) {
    document.querySelector('[data-mobile-menu-toggle]')?.setAttribute('aria-expanded', 'false');
  }
  if (overlay !== activeOverlay) return;
  activeOverlay = null;
  document.body.classList.remove('is-locked');
  if (restoreFocus) activeOverlayTrigger?.focus();
  activeOverlayTrigger = null;
}

function trapFocus(event, container) {
  if (!container || event.key !== 'Tab') return;
  const focusable = focusableElements(container);
  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!container.contains(document.activeElement)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function trapOverlayFocus(event) {
  trapFocus(event, activeOverlay);
}

function openMobileMenu(toggle) {
  const menu = document.querySelector('[data-mobile-menu]');
  if (!menu) return;
  openOverlay(menu, toggle);
  toggle?.setAttribute('aria-expanded', 'true');
}

function closeMobileMenu(restoreFocus = true) {
  const menu = document.querySelector('[data-mobile-menu]');
  if (!menu) return;
  closeOverlay(menu, restoreFocus);
}

function closeFacets(facets) {
  if (!facets) return;
  facets.open = false;
  facets.querySelector('[data-filter-trigger], summary')?.focus();
}

function applyGalleryImage(image, media, sizes = media.sizes) {
  if (!image || !media) return;

  if (sizes) image.setAttribute('sizes', sizes);
  if (media.srcset) image.setAttribute('srcset', media.srcset);
  else image.removeAttribute('srcset');
  if (media.src) image.src = media.src;
  if (media.width) image.width = Number(media.width);
  if (media.height) image.height = Number(media.height);
  image.alt = media.alt || '';
  image.setAttribute('data-media-id', String(media.id));
}

function setGalleryImage(section, mediaId) {
  const gallery = section?.querySelector('[data-product-gallery]');
  gallery?.galleryController?.selectMedia(String(mediaId));
}

function setupProductGallery(gallery) {
  const mediaElement = gallery.querySelector('[data-product-media]');
  const mainWrapper = gallery.querySelector('[data-product-main-image]');
  const mainImage = mainWrapper?.querySelector('img');
  const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
  const announcer = gallery.querySelector('[data-gallery-announcer]');
  const dialog = gallery.querySelector('[data-gallery-dialog]');
  const dialogImage = dialog?.querySelector('[data-gallery-dialog-image]');
  const dialogStatus = dialog?.querySelector('[data-gallery-dialog-status]');
  const dialogClose = dialog?.querySelector('[data-gallery-dialog-close]');

  if (!mediaElement || !mainWrapper || !mainImage) return;

  let media = [];
  try {
    media = JSON.parse(mediaElement.textContent);
  } catch (error) {
    return;
  }
  if (media.length === 0) return;

  const mediaById = new Map(media.map((item) => [String(item.id), item]));
  const initialMediaId = String(mainWrapper.dataset.currentMediaId || media[0].id);
  let currentIndex = Math.max(0, media.findIndex((item) => String(item.id) === initialMediaId));
  let dialogTrigger = null;
  let touchStartX = null;
  let touchStartY = null;

  mainImage.setAttribute('data-media-id', String(media[currentIndex].id));

  const updateDialog = (item) => {
    if (!dialog?.open || !dialogImage) return;
    applyGalleryImage(dialogImage, { ...item, src: item.full_src || item.src }, '100vw');
    if (dialogStatus) dialogStatus.textContent = item.status || item.alt;
  };

  const selectMedia = (mediaId, { announce = true } = {}) => {
    const item = mediaById.get(String(mediaId));
    if (!item) return;

    const nextIndex = media.indexOf(item);
    const hasChanged = nextIndex !== currentIndex;
    currentIndex = nextIndex;
    mainWrapper.dataset.currentMediaId = String(item.id);
    applyGalleryImage(mainImage, item);

    thumbs.forEach((thumb) => {
      thumb.setAttribute('aria-current', String(thumb.dataset.mediaId) === String(item.id) ? 'true' : 'false');
    });

    updateDialog(item);
    if (announce && hasChanged && announcer) announcer.textContent = item.status || item.alt;
  };

  const move = (direction, options) => {
    const nextIndex = (currentIndex + direction + media.length) % media.length;
    selectMedia(media[nextIndex].id, options);
  };

  const focusCurrentThumb = () => {
    const currentId = String(media[currentIndex].id);
    thumbs.find((thumb) => String(thumb.dataset.mediaId) === currentId)?.focus();
  };

  const openDialog = (trigger) => {
    if (!dialog || typeof dialog.showModal !== 'function') return;
    dialogTrigger = trigger;
    dialog.showModal();
    document.body.classList.add('is-locked');
    updateDialog(media[currentIndex]);
    window.requestAnimationFrame(() => dialogClose?.focus());
  };

  gallery.galleryController = {
    selectMedia,
    next: () => move(1),
    previous: () => move(-1),
  };

  thumbs.forEach((thumb) => {
    thumb.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();

      if (event.key === 'Home') selectMedia(media[0].id);
      else if (event.key === 'End') selectMedia(media[media.length - 1].id);
      else move(event.key === 'ArrowRight' ? 1 : -1);
      focusCurrentThumb();
    });
  });

  gallery.querySelector('[data-gallery-previous]')?.addEventListener('click', () => move(-1));
  gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => move(1));
  gallery.querySelector('[data-gallery-zoom]')?.addEventListener('click', (event) => openDialog(event.currentTarget));
  dialog?.querySelector('[data-gallery-dialog-previous]')?.addEventListener('click', () => move(-1));
  dialog?.querySelector('[data-gallery-dialog-next]')?.addEventListener('click', () => move(1));
  dialogClose?.addEventListener('click', () => dialog.close());

  dialog?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      dialog.close();
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      move(event.key === 'ArrowRight' ? 1 : -1);
      return;
    }
    trapFocus(event, dialog);
  });

  dialog?.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  dialog?.addEventListener('close', () => {
    document.body.classList.remove('is-locked');
    dialogTrigger?.focus();
    dialogTrigger = null;
  });

  mainWrapper.addEventListener('touchstart', (event) => {
    if (event.touches.length !== 1) return;
    touchStartX = event.touches[0].clientX;
    touchStartY = event.touches[0].clientY;
  }, { passive: true });

  mainWrapper.addEventListener('touchend', (event) => {
    if (touchStartX === null || touchStartY === null || event.changedTouches.length !== 1) return;
    const deltaX = event.changedTouches[0].clientX - touchStartX;
    const deltaY = event.changedTouches[0].clientY - touchStartY;
    touchStartX = null;
    touchStartY = null;

    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    move(deltaX < 0 ? 1 : -1);
  }, { passive: true });
}

function setupProductForm(form) {
  const variantsElement = form.querySelector('[data-product-variants]');
  const variantSelect = form.querySelector('[data-variant-select]');
  const optionInputs = Array.from(form.querySelectorAll('[data-option-input]'));
  const section = form.closest('[data-product-section]');

  if (!variantsElement || !variantSelect || !section) return;

  let variants = [];
  try {
    variants = JSON.parse(variantsElement.textContent);
  } catch (error) {
    return;
  }

  const selectedOptions = () => {
    const values = [];
    optionInputs.forEach((input) => {
      if (input.tagName !== 'SELECT' && !input.checked) return;
      if (!input.value) return;
      values[Number(input.dataset.optionPosition) - 1] = input.value;
    });
    return values;
  };

  const findSelectedVariant = () => {
    if (optionInputs.length === 0) {
      return variants.find((variant) => String(variant.id) === variantSelect.value);
    }

    const options = selectedOptions();
    if (options.length !== variants[0]?.options.length || options.some((value) => !value)) return null;
    return variants.find((variant) => variant.options.every((value, index) => value === options[index]));
  };

  const updateOptionAvailability = () => {
    if (optionInputs.length === 0) return;
    const currentOptions = selectedOptions();

    optionInputs.forEach((input) => {
      const optionIndex = Number(input.dataset.optionPosition) - 1;
      const isAvailable = (value) => variants.some((variant) => (
        variant.available
        && variant.options[optionIndex] === value
        && variant.options.every((variantValue, index) => (
          index === optionIndex || !currentOptions[index] || variantValue === currentOptions[index]
        ))
      ));

      if (input.tagName === 'SELECT') {
        Array.from(input.options).forEach((option) => {
          if (!option.value) return;
          option.disabled = !isAvailable(option.value);
        });
        return;
      }

      const available = isAvailable(input.value);

      input.disabled = !available;
      const label = form.querySelector(`label[for="${input.id}"]`);
      if (label) label.classList.toggle('is-unavailable', !available);
    });
  };

  const renderVariantMedia = (variant) => {
    if (!variant?.featured_media_id) return;
    setGalleryImage(section, variant.featured_media_id);
  };

  const renderVariant = (variant) => {
    const price = section.querySelector('[data-product-price]');
    const comparePrice = section.querySelector('[data-compare-price]');
    const submit = form.querySelector('[data-add-to-cart]');
    const validation = form.querySelector('[data-variant-validation]');
    const stickyPrice = section.querySelector('[data-sticky-price]');
    const stickyButton = section.querySelector('[data-sticky-add]');

    if (!variant) {
      const currentOptions = selectedOptions();
      const mediaVariant = variants.find((item) => (
        item.featured_media_id
        && item.options.every((value, index) => !currentOptions[index] || value === currentOptions[index])
      ));
      renderVariantMedia(mediaVariant);

      if (submit) {
        submit.disabled = true;
        submit.textContent = submit.getAttribute('data-select-label');
      }
      if (validation) validation.hidden = false;
      if (stickyButton) stickyButton.disabled = true;
      updateOptionAvailability();
      return;
    }

    variantSelect.value = String(variant.id);
    if (price) price.textContent = variant.price;
    if (stickyPrice) stickyPrice.textContent = variant.price;

    if (comparePrice) {
      comparePrice.hidden = !variant.compare_at_price;
      comparePrice.textContent = variant.compare_at_price || '';
    }

    if (submit) {
      submit.disabled = !variant.available;
      submit.textContent = variant.available
        ? submit.getAttribute('data-add-label')
        : submit.getAttribute('data-sold-out-label');
    }
    if (validation) validation.hidden = true;
    if (stickyButton) stickyButton.disabled = !variant.available;

    renderVariantMedia(variant);

    if (variant.url) window.history.replaceState({}, '', variant.url);
    updateOptionAvailability();
  };

  optionInputs.forEach((input) => {
    input.addEventListener('change', () => renderVariant(findSelectedVariant()));
  });

  variantSelect.addEventListener('change', () => {
    const variant = variants.find((item) => String(item.id) === variantSelect.value);
    renderVariant(variant);
  });

  renderVariant(findSelectedVariant());
}

function productFormError(form, message = '') {
  const error = form.querySelector('[data-product-error]');
  if (!error) return;
  error.textContent = message;
  error.hidden = !message;
}

let latestCart = null;
let cartQueue = Promise.resolve();
let cartOperations = 0;
let cartStateUncertain = false;
let cartCheckoutPending = false;
let cartFocusAfterUpdate = null;
const confirmedCartAdds = new WeakMap();

const cartCopyDefaults = {
  updating: 'Updating your cart…',
  error: 'The cart could not be updated. Please try again.',
  refresh_error: 'Your cart could not be loaded. Check your cart before trying again.',
  added_recovery: 'Added to your cart. The latest cart details could not be loaded.',
  update_recovery: 'Your change may have been saved. Refresh your cart before changing it again.',
  view: 'View cart', retry: 'Refresh cart', quantity: 'Quantity',
  increase: 'Increase quantity', decrease: 'Decrease quantity', remove: 'Remove',
  count: '{{ count }} items', updated_summary: 'Cart updated. {{ count }} items, subtotal {{ amount }}.',
  shipping_remaining: "You're {{ amount }} away from free U.S. shipping.",
  shipping_qualified: 'You qualify for free U.S. shipping.',
  invalid_quantity: 'Enter a whole number of zero or more.',
  checkout_pending: 'Preparing checkout. Please wait.',
};
let cartTranslations = {};
try {
  cartTranslations = JSON.parse(document.querySelector('[data-cart-translations]')?.textContent || '{}');
} catch (error) {
  cartTranslations = {};
}

function cartText(key, values = {}) {
  let text = cartTranslations[key] || cartCopyDefaults[key] || '';
  Object.entries(values).forEach(([name, value]) => {
    text = text.replaceAll(`{{ ${name} }}`, String(value)).replaceAll(`{{${name}}}`, String(value));
  });
  return text;
}

function announceCart(message) {
  const drawer = document.querySelector('[data-cart-drawer]');
  const live = (!drawer?.hidden && drawer?.querySelector('[data-cart-live]'))
    || document.querySelector('[data-cart-page] [data-cart-live]')
    || document.querySelector('[data-cart-live]');
  if (live) live.textContent = message;
}

function cartNotice(message = '', { recovery = false } = {}) {
  const drawer = document.querySelector('[data-cart-drawer-body]');
  if (drawer && !drawer.querySelector('[data-cart-notice]')) {
    const notice = document.createElement('div');
    notice.className = 'product-form-error';
    notice.setAttribute('data-cart-notice', '');
    notice.setAttribute('role', 'alert');
    notice.hidden = true;
    drawer.prepend(notice);
  }
  document.querySelectorAll('[data-cart-notice]').forEach((notice) => {
    notice.replaceChildren();
    notice.hidden = !message;
    if (!message) return;
    const text = document.createElement('p');
    text.textContent = message;
    notice.append(text);
    if (recovery) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'text-button';
      retry.setAttribute('data-cart-refresh', '');
      retry.textContent = cartText('retry');
      const link = document.createElement('a');
      link.href = `${shopRoot}cart`;
      link.className = 'text-link';
      link.textContent = cartText('view');
      notice.append(retry, document.createTextNode(' · '), link);
    }
  });
  if (message) announceCart(message);
}

function setCartBusy(busy) {
  busy = busy || cartCheckoutPending;
  document.querySelectorAll('[data-cart-items], [data-cart-page-form]').forEach((region) => {
    region.setAttribute('aria-busy', String(busy));
  });
  document.querySelectorAll('[data-cart-quantity], [data-cart-page-form] [name="update"]').forEach((control) => {
    if (busy && !control.disabled) {
      control.disabled = true;
      control.setAttribute('data-cart-busy-disabled', '');
    } else if (!busy && control.hasAttribute('data-cart-busy-disabled')) {
      control.disabled = false;
      control.removeAttribute('data-cart-busy-disabled');
    }
  });
  document.querySelectorAll('[data-cart-accelerated-checkout]').forEach((element) => {
    element.inert = busy;
    element.setAttribute('aria-disabled', String(busy));
  });
  document.querySelectorAll('[data-cart-checkout]').forEach((control) => {
    control.setAttribute('aria-busy', String(busy));
    if (cartCheckoutPending && !control.hasAttribute('data-cart-checkout-label')) {
      control.setAttribute('data-cart-checkout-label', control.textContent);
      control.textContent = cartText('checkout_pending');
    } else if (!cartCheckoutPending && control.hasAttribute('data-cart-checkout-label')) {
      control.textContent = control.getAttribute('data-cart-checkout-label');
      control.removeAttribute('data-cart-checkout-label');
    }
  });
  document.querySelectorAll('[data-cart-change], [data-cart-remove], [data-add-to-cart]').forEach((control) => {
    if (cartCheckoutPending) {
      if (!control.hasAttribute('data-cart-checkout-lock')) {
        control.setAttribute('data-cart-checkout-lock', control.getAttribute('aria-disabled') || '');
      }
      control.setAttribute('aria-disabled', 'true');
      if ('disabled' in control && !control.matches('[data-add-to-cart]') && !control.disabled) {
        control.disabled = true;
        control.setAttribute('data-cart-checkout-disabled', '');
      }
    } else if (control.hasAttribute('data-cart-checkout-lock')) {
      const previous = control.getAttribute('data-cart-checkout-lock');
      if (previous) control.setAttribute('aria-disabled', previous);
      else control.removeAttribute('aria-disabled');
      control.removeAttribute('data-cart-checkout-lock');
      if (control.hasAttribute('data-cart-checkout-disabled')) {
        control.disabled = false;
        control.removeAttribute('data-cart-checkout-disabled');
      }
      const form = control.closest('[data-product-form]');
      if (form?.querySelector('[data-product-error]')?.textContent === cartText('checkout_pending')) productFormError(form);
    }
  });
  if (!busy && cartFocusAfterUpdate) {
    if (cartFocusAfterUpdate.isConnected) cartFocusAfterUpdate.focus();
    cartFocusAfterUpdate = null;
  }
}

function queueCartOperation(operation) {
  cartOperations += 1;
  setCartBusy(true);
  if (cartOperations === 1) announceCart(cartText('updating'));
  const result = cartQueue.then(operation);
  cartQueue = result.catch(() => {}).finally(() => {
    cartOperations -= 1;
    setCartBusy(cartOperations > 0);
  });
  return result;
}

async function requestCart(url, options = {}) {
  const response = await fetch(url, { ...options, headers: { Accept: 'application/json', ...options.headers } });
  const data = await response.json();
  if (!response.ok) throw new Error(data.description || data.message || cartText('error'));
  return data;
}

async function refreshCart() {
  const cart = await requestCart(`${shopRoot}cart.js`);
  if (!Array.isArray(cart.items)) throw new Error(cartText('refresh_error'));
  renderCart(cart);
  cartStateUncertain = false;
  return cart;
}

async function ensureCartState() {
  if (!latestCart || cartStateUncertain) await refreshCart();
  return latestCart;
}

function cartItemImage(item, width, height) {
  if (!item.image) return '';
  const url = new URL(item.image, window.location.origin);
  url.searchParams.set('width', String(width * 2));
  return `<img src="${escapeHTML(url.href)}" alt="${escapeHTML(item.product_title)}" width="${width}" height="${height}" loading="lazy">`;
}

function cartItemDetails(item) {
  const variant = item.variant_title && item.variant_title !== 'Default Title'
    ? `<p>${escapeHTML(item.variant_title)}</p>` : '';
  const properties = Object.entries(item.properties || {}).filter(([key, value]) => !key.startsWith('_') && value != null && value !== '')
    .map(([key, value]) => `<p>${escapeHTML(key)}: ${escapeHTML(value)}</p>`).join('');
  const plan = item.selling_plan_allocation?.selling_plan?.name;
  return variant + properties + (plan ? `<p>${escapeHTML(plan)}</p>` : '');
}

function cartMerchandiseSubtotal(cart) {
  // Line totals already include line discounts. Cart discounts are applied separately;
  // delivery discounts must never reduce the merchandise subtotal or shipping threshold.
  const money = (value) => {
    if (!Number.isSafeInteger(value) || value < 0) throw new Error(cartText('refresh_error'));
    return value;
  };
  const itemsSubtotal = cart.items_subtotal_price == null
    ? cart.items.reduce((sum, item) => sum + money(item.final_line_price), 0)
    : money(cart.items_subtotal_price);
  const cartDiscount = (cart.cart_level_discount_applications || []).reduce((sum, discount) => (
    discount.target_type === 'line_item' ? sum + money(discount.total_allocated_amount) : sum
  ), 0);
  return Math.max(0, itemsSubtotal - cartDiscount);
}

function renderCart(cart) {
  const subtotal = cartMerchandiseSubtotal(cart);
  latestCart = cart;
  const focused = document.activeElement;
  const focusKey = focused?.dataset.cartKey;
  const focusDelta = focused?.dataset.cartDelta;
  const focusOnQuantity = focused?.hasAttribute('data-cart-quantity');
  const focusOnRemove = focused?.hasAttribute('data-cart-remove') || focused?.dataset.quantity === '0';
  const focusInPage = Boolean(focused?.closest('[data-cart-page]'));
  const count = cart.item_count;
  const remaining = FREE_SHIPPING_THRESHOLD - subtotal;

  document.querySelectorAll('[data-cart-count]').forEach((element) => { element.textContent = count; element.hidden = count === 0; });
  document.querySelectorAll('[data-cart-drawer-count]').forEach((element) => { element.textContent = count > 0 ? `(${count})` : ''; });
  document.querySelectorAll('[data-cart-page-count]').forEach((element) => { element.textContent = cartText('count', { count }); element.hidden = count === 0; });
  document.querySelectorAll('[data-cart-subtotal]').forEach((element) => { element.textContent = formatMoney(subtotal); });
  document.querySelectorAll('[data-shipping-message]').forEach((element) => {
    element.textContent = remaining > 0 ? cartText('shipping_remaining', { amount: formatMoney(remaining) }) : cartText('shipping_qualified');
  });
  document.querySelectorAll('[data-shipping-progress]').forEach((element) => { element.style.width = `${Math.min(100, subtotal / FREE_SHIPPING_THRESHOLD * 100)}%`; });
  document.querySelectorAll('[data-cart-items]').forEach((container) => {
    container.innerHTML = cart.items.map((item) => `
      <article class="cart-drawer-item" data-cart-key="${escapeHTML(item.key)}">
        <a class="cart-drawer-image" href="${escapeHTML(item.url)}">${cartItemImage(item, 96, 120)}</a>
        <div class="cart-drawer-item-info">
          <a href="${escapeHTML(item.url)}"><strong>${escapeHTML(item.product_title)}</strong></a>
          ${cartItemDetails(item)}
          <div class="quantity-stepper">
            <button type="button" data-cart-change data-cart-key="${escapeHTML(item.key)}" data-cart-delta="-1" aria-label="${escapeHTML(cartText('decrease'))}">&minus;</button>
            <span>${item.quantity}</span>
            <button type="button" data-cart-change data-cart-key="${escapeHTML(item.key)}" data-cart-delta="1" aria-label="${escapeHTML(cartText('increase'))}">+</button>
          </div>
          <button class="text-button" type="button" data-cart-change data-cart-key="${escapeHTML(item.key)}" data-quantity="0">${escapeHTML(cartText('remove'))}</button>
        </div>
        <strong>${formatMoney(item.final_line_price)}</strong>
      </article>`).join('');
    container.hidden = count === 0;
  });
  document.querySelectorAll('[data-cart-page-items]').forEach((container) => {
    container.innerHTML = cart.items.map((item, index) => `
      <article class="cart-line" data-cart-key="${escapeHTML(item.key)}">
        <a class="cart-line-image" href="${escapeHTML(item.url)}">${cartItemImage(item, 132, 165)}</a>
        <div class="cart-line-info">
          ${item.product_type ? `<p class="cart-line-type">${escapeHTML(item.product_type)}</p>` : ''}
          <a href="${escapeHTML(item.url)}"><strong>${escapeHTML(item.product_title)}</strong></a>
          ${cartItemDetails(item)}
          <a class="text-button" data-cart-remove data-cart-key="${escapeHTML(item.key)}" href="${shopRoot}cart/change?id=${encodeURIComponent(item.key)}&quantity=0">${escapeHTML(cartText('remove'))}</a>
        </div>
        <div class="cart-line-quantity">
          <label class="visually-hidden" for="CartQuantity-${index + 1}">${escapeHTML(cartText('quantity'))}: ${escapeHTML(item.product_title)}</label>
          <input id="CartQuantity-${index + 1}" type="number" name="updates[]" value="${item.quantity}" min="0" step="1" inputmode="numeric" data-cart-quantity data-cart-key="${escapeHTML(item.key)}" data-cart-original-quantity="${item.quantity}">
        </div>
        <div class="cart-line-price">${item.original_line_price > item.final_line_price ? `<s>${formatMoney(item.original_line_price)}</s>` : ''}<strong>${formatMoney(item.final_line_price)}</strong></div>
      </article>`).join('');
  });
  document.querySelectorAll('[data-cart-empty]').forEach((element) => { element.hidden = count > 0; });
  document.querySelectorAll('[data-cart-footer], [data-cart-page-form]').forEach((element) => { element.hidden = count === 0; });
  setCartBusy(cartOperations > 0);

  if (focusKey && !focused.isConnected) {
    const scope = document.querySelector(focusInPage ? '[data-cart-page]' : '[data-cart-drawer]');
    const replacement = Array.from(scope?.querySelectorAll('[data-cart-key]') || []).find((element) => (
      element.dataset.cartKey === focusKey
      && (focusOnQuantity ? element.hasAttribute('data-cart-quantity')
        : focusOnRemove ? element.hasAttribute('data-cart-remove') || element.dataset.quantity === '0'
          : focusDelta !== undefined && element.dataset.cartDelta === focusDelta)
    ));
    const target = replacement || scope?.querySelector(count ? '[data-cart-change], [data-cart-remove]' : '[data-cart-empty] a');
    if (target?.disabled) cartFocusAfterUpdate = target;
    else target?.focus();
  }
  announceCart(cartText('updated_summary', { count, amount: formatMoney(subtotal) }));
}

async function recoverCartAfterError(error) {
  cartStateUncertain = true;
  try {
    await refreshCart();
    cartNotice(error.message || cartText('error'));
  } catch (refreshError) {
    cartNotice(cartText('update_recovery'), { recovery: true });
  }
}

function changeCart(key, change) {
  if (cartCheckoutPending) {
    announceCart(cartText('checkout_pending'));
    return Promise.resolve();
  }
  if (!key) {
    cartNotice(cartText('refresh_error'), { recovery: true });
    return Promise.resolve();
  }
  return queueCartOperation(async () => {
    cartNotice();
    try {
      await ensureCartState();
      const item = latestCart.items.find((entry) => entry.key === key);
      if (!item) throw new Error(cartText('refresh_error'));
      const quantity = change.delta !== undefined ? Math.max(0, item.quantity + change.delta) : change.quantity;
      if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error(cartText('invalid_quantity'));
      if (quantity === item.quantity) return;
      cartStateUncertain = true;
      const cart = await requestCart(`${shopRoot}cart/change.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }),
      });
      if (!Array.isArray(cart.items)) throw new Error(cartText('refresh_error'));
      renderCart(cart);
      cartStateUncertain = false;
    } catch (error) {
      await recoverCartAfterError(error);
    }
  });
}

function cartFormChanges(form) {
  return Array.from(form?.querySelectorAll('[data-cart-quantity]') || []).filter((input) => input.value !== input.dataset.cartOriginalQuantity)
    .map((input) => {
      const quantity = Number(input.value);
      if (input.value.trim() === '' || !Number.isSafeInteger(quantity) || quantity < 0) throw new Error(cartText('invalid_quantity'));
      return { key: input.dataset.cartKey, quantity };
    });
}

function submitCart(form, checkout = false) {
  if (cartCheckoutPending) {
    announceCart(cartText('checkout_pending'));
    return;
  }
  let changes;
  try {
    changes = cartFormChanges(form);
  } catch (error) {
    cartNotice(error.message);
    return;
  }
  if (checkout) cartCheckoutPending = true;
  return queueCartOperation(async () => {
    cartNotice();
    let leavingForCheckout = false;
    try {
      await refreshCart();
      for (const { key, quantity } of changes) {
        const item = latestCart.items.find((entry) => entry.key === key);
        if (!item) throw new Error(cartText('refresh_error'));
        if (item.quantity === quantity) continue;
        cartStateUncertain = true;
        const cart = await requestCart(`${shopRoot}cart/change.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }),
        });
        if (!Array.isArray(cart.items)) throw new Error(cartText('refresh_error'));
        renderCart(cart);
        cartStateUncertain = false;
      }
      if (checkout && latestCart.item_count > 0) {
        announceCart(cartText('checkout_pending'));
        window.location.assign(`${shopRoot}checkout`);
        leavingForCheckout = true;
      }
    } catch (error) {
      await recoverCartAfterError(error);
    } finally {
      if (checkout && !leavingForCheckout) cartCheckoutPending = false;
    }
  });
}

function refreshCartWithFeedback() {
  return queueCartOperation(async () => {
    try {
      await refreshCart();
      cartNotice();
      return true;
    } catch (error) {
      cartStateUncertain = true;
      cartNotice(cartText('refresh_error'), { recovery: true });
      return false;
    }
  });
}

function addProductToCart(form, submit) {
  if (cartCheckoutPending) {
    productFormError(form, cartText('checkout_pending'));
    announceCart(cartText('checkout_pending'));
    return;
  }
  if (form.hasAttribute('data-cart-add-pending')) return;
  const payload = new FormData(form);
  const signature = JSON.stringify(Array.from(payload.entries(), ([name, value]) => [name,
    typeof value === 'string' ? value : `${value.name}:${value.size}:${value.lastModified}`]));
  const previousAdd = confirmedCartAdds.get(form);
  if (previousAdd?.signature === signature) {
    return refreshCartWithFeedback().then((recovered) => {
      if (recovered) {
        confirmedCartAdds.delete(form);
        form.removeAttribute('data-cart-add-confirmed');
        productFormError(form);
        submit.textContent = previousAdd.label;
      }
      openOverlay(document.querySelector('[data-cart-drawer]'), submit);
    });
  }
  confirmedCartAdds.delete(form);
  form.removeAttribute('data-cart-add-confirmed');
  const originalLabel = submit.textContent;
  const loadingLabel = submit.getAttribute('data-loading-label') || 'Adding...';
  form.setAttribute('data-cart-add-pending', '');
  submit.disabled = true;
  submit.setAttribute('aria-busy', 'true');
  submit.textContent = loadingLabel;
  productFormError(form);

  return queueCartOperation(async () => {
    let added = false;
    let responseReceived = false;
    try {
      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: 'POST', headers: { Accept: 'application/json' }, body: payload,
      });
      responseReceived = true;
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || data.message || cartText('error'));
      }
      added = true;
      confirmedCartAdds.set(form, { signature, label: originalLabel });
      form.setAttribute('data-cart-add-confirmed', '');
      cartStateUncertain = true;
      await refreshCart();
      form.removeAttribute('data-cart-add-confirmed');
      confirmedCartAdds.delete(form);
      cartNotice();
      openOverlay(document.querySelector('[data-cart-drawer]'), submit);
    } catch (error) {
      if (added || !responseReceived) {
        confirmedCartAdds.set(form, { signature, label: originalLabel });
        form.setAttribute('data-cart-add-confirmed', '');
        cartStateUncertain = true;
        const message = cartText(added ? 'added_recovery' : 'update_recovery');
        productFormError(form, message);
        cartNotice(message, { recovery: true });
        openOverlay(document.querySelector('[data-cart-drawer]'), submit);
      } else {
        productFormError(form, error.message || cartText('error'));
      }
    } finally {
      form.removeAttribute('data-cart-add-pending');
      submit.removeAttribute('aria-busy');
      if (submit.textContent === loadingLabel) {
        submit.disabled = false;
        submit.textContent = form.hasAttribute('data-cart-add-confirmed') ? cartText('view') : originalLabel;
      }
    }
  });
}


function setupPredictiveSearch() {
  const form = document.querySelector('[data-predictive-search-form]');
  const input = form?.querySelector('[data-predictive-search-input]');
  const results = document.querySelector('[data-predictive-results]');
  const status = document.querySelector('[data-predictive-status]');
  if (!form || !input || !results || !status) return;

  const initialHTML = results.innerHTML;
  let timer;
  let controller;

  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    controller?.abort();
    const query = input.value.trim();

    if (query.length < 2) {
      status.textContent = '';
      results.innerHTML = initialHTML;
      return;
    }

    timer = window.setTimeout(async () => {
      controller = new AbortController();
      status.textContent = 'Searching...';
      try {
        const parameters = new URLSearchParams({
          q: query,
          'resources[type]': 'product,collection',
          'resources[limit]': '5',
          'resources[options][unavailable_products]': 'last',
        });
        const response = await fetch(`${shopRoot}search/suggest.json?${parameters}`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Search failed');
        const data = await response.json();
        const products = data.resources?.results?.products || [];
        const collections = data.resources?.results?.collections || [];

        const productHTML = products.length > 0 ? `
          <div>
            <p class="overlay-label">Products</p>
            <div class="predictive-product-grid">
              ${products.slice(0, 5).map((product) => {
                const imageValue = product.image || product.featured_image;
                const imageURL = typeof imageValue === 'string' ? imageValue : imageValue?.url;
                const image = imageURL ? `<img src="${escapeHTML(imageURL)}&width=480" alt="" width="320" height="400">` : '';
                const rawPrice = String(product.price || '');
                const numericPrice = Number(rawPrice.replace(/[^0-9.-]/g, ''));
                const displayPrice = rawPrice && Number.isFinite(numericPrice)
                  ? formatMoney(Math.round(numericPrice * 100))
                  : rawPrice;
                return `<a class="predictive-product" href="${escapeHTML(product.url)}">${image}<strong>${escapeHTML(product.title)}</strong><span>${escapeHTML(displayPrice)}</span></a>`;
              }).join('')}
            </div>
          </div>` : '';

        const collectionHTML = collections.length > 0 ? `
          <div>
            <p class="overlay-label">Collections</p>
            <div class="search-collection-links">${collections.slice(0, 4).map((collection) => `<a href="${escapeHTML(collection.url)}">${escapeHTML(collection.title)}</a>`).join('')}</div>
          </div>` : '';

        results.innerHTML = `${productHTML}${collectionHTML}<a class="arrow-link predictive-view-all" href="${form.action}?q=${encodeURIComponent(query)}&type=product">View all results for &quot;${escapeHTML(query)}&quot; &rarr;</a>`;
        status.textContent = products.length + collections.length > 0 ? '' : `No suggestions for "${query}".`;
      } catch (error) {
        if (error.name !== 'AbortError') status.textContent = 'Search suggestions are unavailable. Press Enter to search.';
      }
    }, 220);
  });
}

function setupStickyAddToCart() {
  const form = document.querySelector('[data-product-form]');
  const sticky = document.querySelector('[data-sticky-atc]');
  if (!form || !sticky || !('IntersectionObserver' in window)) return;

  sticky.hidden = false;
  const observer = new IntersectionObserver(([entry]) => {
    sticky.classList.toggle('is-visible', !entry.isIntersecting);
  }, { threshold: 0.05 });
  observer.observe(form);
}

document.addEventListener('click', (event) => {
  const mobileToggle = event.target.closest('[data-mobile-menu-toggle]');
  if (mobileToggle) {
    openMobileMenu(mobileToggle);
    return;
  }

  if (event.target.closest('[data-mobile-menu-close]')) {
    closeMobileMenu();
    return;
  }

  if (event.target.closest('[data-mobile-menu] a')) {
    closeMobileMenu(false);
    return;
  }

  const searchOpen = event.target.closest('[data-search-open]');
  if (searchOpen) {
    openOverlay(document.querySelector('[data-search-overlay]'), searchOpen);
    return;
  }

  if (event.target.closest('[data-search-close]')) {
    closeOverlay(document.querySelector('[data-search-overlay]'));
    return;
  }

  const cartOpen = event.target.closest('[data-cart-open]');
  if (cartOpen) {
    openOverlay(document.querySelector('[data-cart-drawer]'), cartOpen);
    return;
  }

  if (event.target.closest('[data-cart-close]')) {
    closeOverlay(document.querySelector('[data-cart-drawer]'));
    return;
  }

  const overlayClose = event.target.closest('[data-overlay-close]');
  if (overlayClose) {
    closeOverlay(overlayClose.closest('[data-search-overlay], [data-cart-drawer]'));
    return;
  }

  const cartRefresh = event.target.closest('[data-cart-refresh]');
  if (cartRefresh) {
    event.preventDefault();
    refreshCartWithFeedback();
    return;
  }

  const cartCheckout = event.target.closest('a[data-cart-checkout]');
  if (cartCheckout) {
    event.preventDefault();
    submitCart(document.querySelector('[data-cart-page-form]'), true);
    return;
  }

  const cartChange = event.target.closest('[data-cart-change], [data-cart-remove]');
  if (cartChange) {
    event.preventDefault();
    const key = cartChange.dataset.cartKey || cartChange.closest('[data-cart-key]')?.dataset.cartKey;
    const change = cartChange.hasAttribute('data-cart-delta')
      ? { delta: Number(cartChange.dataset.cartDelta) }
      : { quantity: cartChange.hasAttribute('data-cart-remove') ? 0 : Number(cartChange.dataset.quantity) };
    changeCart(key, change);
    return;
  }

  const thumb = event.target.closest('[data-gallery-thumb]');
  if (thumb) {
    const section = thumb.closest('[data-product-section]');
    if (section) setGalleryImage(section, thumb.dataset.mediaId);
    return;
  }

  const quantityButton = event.target.closest('[data-product-quantity-change]');
  if (quantityButton) {
    const input = quantityButton.closest('[data-product-quantity]')?.querySelector('input');
    if (!input) return;
    const nextValue = Math.max(1, Number(input.value || 1) + Number(quantityButton.dataset.productQuantityChange));
    input.value = nextValue;
    return;
  }

  const stickyAdd = event.target.closest('[data-sticky-add]');
  if (stickyAdd) {
    document.querySelector('[data-product-form]')?.requestSubmit(document.querySelector('[data-add-to-cart]'));
    return;
  }

  const sizeGuideOpen = event.target.closest('[data-size-guide-open]');
  if (sizeGuideOpen) {
    document.querySelector('[data-size-guide-dialog]')?.showModal();
    return;
  }

  const sizeGuideClose = event.target.closest('[data-size-guide-close]');
  if (sizeGuideClose) {
    sizeGuideClose.closest('dialog')?.close();
    return;
  }

  if (event.target.closest('[data-facets-close]')) {
    closeFacets(event.target.closest('.facets-shell'));
  }
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-cart-quantity]');
  if (!input || input.value === input.dataset.cartOriginalQuantity) return;
  if (input.value.trim() === '' || !input.checkValidity()) {
    cartNotice(cartText('invalid_quantity'));
    return;
  }
  changeCart(input.dataset.cartKey, { quantity: Number(input.value) });
});

document.addEventListener('submit', (event) => {
  const cartForm = event.target.closest('[data-cart-page-form]');
  if (cartForm) {
    event.preventDefault();
    submitCart(cartForm, event.submitter?.name === 'checkout');
    return;
  }
  const form = event.target.closest('[data-product-form]');
  const submit = event.submitter;
  if (!form || !submit?.matches('[data-add-to-cart]')) return;
  event.preventDefault();
  addProductToCart(form, submit);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    if (activeOverlay) {
      event.preventDefault();
      closeOverlay(activeOverlay);
      return;
    }
    const facets = document.querySelector('.facets-shell[open]');
    if (facets) {
      event.preventDefault();
      closeFacets(facets);
    }
  }
  trapOverlayFocus(event);
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  cartCheckoutPending = false;
  setCartBusy(cartOperations > 0);
  refreshCartWithFeedback();
});

document.querySelectorAll('[data-product-gallery]').forEach(setupProductGallery);
document.querySelectorAll('[data-product-form]').forEach(setupProductForm);
document.querySelectorAll('[data-sort-select]').forEach((select) => {
  select.addEventListener('change', () => select.form?.submit());
});
setupPredictiveSearch();
setupStickyAddToCart();
